import { useEffect, useState } from 'react';
import * as api from '../api';

export default function QuizzesPage({ user }) {
  const [rows, setRows] = useState([]);
  const [videos, setVideos] = useState([]);
  const [myAttempts, setMyAttempts] = useState({}); // quiz_id -> best attempt
  const [err, setErr] = useState('');
  const [modal, setModal] = useState(false);
  const [formErr, setFormErr] = useState('');
  const [form, setForm] = useState({ title: '', videoId: '', passScore: 75, questions: [{ question_text: '', option_a: '', option_b: '', option_c: '', option_d: '', correct_option: 'a' }] });
  const [confirm, setConfirm] = useState(null);
  const [activeQuiz, setActiveQuiz] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const isAdmin = user?.role === 'admin';

  const load = async () => {
    try {
      const [qRes, vRes] = await Promise.all([api.getQuizzes(), api.getVideos()]);
      setRows(qRes.data || []);
      setVideos(vRes.data || []);
      if (!isAdmin) {
        try {
          const { data } = await api.getMyQuizAttempts();
          const map = {};
          (data || []).forEach(a => { map[a.quiz_id] = a; });
          setMyAttempts(map);
        } catch { /* non-fatal */ }
      }
    } catch (e) { setErr(e.response?.data?.error || 'Could not load'); }
  };

  useEffect(() => { load(); }, []);

  const addQuestion = () => setForm(f => ({ ...f, questions: [...f.questions, { question_text: '', option_a: '', option_b: '', option_c: '', option_d: '', correct_option: 'a' }] }));
  const removeQuestion = (qi) => setForm(f => ({ ...f, questions: f.questions.filter((_, i) => i !== qi) }));

  const handleCreate = async () => {
    setFormErr('');
    if (!form.title.trim()) { setFormErr('Title is required.'); return; }
    if (!form.videoId) { setFormErr('Please select a video.'); return; }
    try {
      await api.createQuiz({ video_id: form.videoId, title: form.title.trim(), pass_score: Number(form.passScore) || 75, questions: form.questions });
      setForm({ title: '', videoId: '', passScore: 75, questions: [{ question_text: '', option_a: '', option_b: '', option_c: '', option_d: '', correct_option: 'a' }] });
      setModal(false);
      load();
    } catch (e) { setFormErr(e.response?.data?.error || 'Create failed'); }
  };

  const handleDelete = async (id) => {
    try { await api.deleteQuiz(id); setConfirm(null); load(); } catch (e) { console.error(e); }
  };

  const startQuiz = async (quiz) => {
    setActiveQuiz(quiz);
    setAnswers({});
    setResult(null);
    try {
      const { data } = await api.getQuizQuestions(quiz.id);
      setQuestions(data || []);
    } catch (e) { console.error(e); setQuestions([]); }
  };

  const submitQuiz = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.submitQuizAttempt(activeQuiz.id, answers);
      setResult(data);
      load();
    } catch (e) { console.error(e); }
    finally { setSubmitting(false); }
  };

  if (err) return <div className="alert">{err}</div>;

  // Quiz taking view (employee)
  if (activeQuiz && !isAdmin) {
    // Result screen
    if (result) {
      const passed = result.passed || result.score >= activeQuiz.pass_score;
      return (
        <div>
          <div className="card" style={{ textAlign: 'center', padding: '48px 20px' }}>
            <div style={{ fontSize: 56, marginBottom: 16 }}>{passed ? '🎉' : '😔'}</div>
            <div style={{ fontSize: 36, fontWeight: 800, color: passed ? 'var(--success)' : 'var(--danger)', marginBottom: 8 }}>{result.score}%</div>
            <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 4 }}>{passed ? 'Congratulations! You passed!' : 'Not quite — keep trying!'}</div>
            <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 8 }}>You got {result.correct} out of {result.total} correct</div>
            <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 28 }}>Required: {activeQuiz.pass_score}% · Your score: {result.score}%</div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              {!passed && <button className="btn btn-primary" onClick={() => { setAnswers({}); setResult(null); }}>Retake Quiz</button>}
              <button className="btn btn-secondary" onClick={() => { setActiveQuiz(null); setResult(null); }}>Back to Quizzes</button>
            </div>
          </div>
        </div>
      );
    }

    // Question view
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>{activeQuiz.title}</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>{questions.length} questions · Pass score: {activeQuiz.pass_score}%</div>
          </div>
          <button className="btn btn-secondary btn-sm" onClick={() => setActiveQuiz(null)}>← Back</button>
        </div>

        {questions.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: 32, color: 'var(--muted)' }}>No questions in this quiz.</div>
        ) : (
          <>
            {questions.map((q, qi) => (
              <div key={q.id} className="card" style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 14 }}>Q{qi + 1}: {q.question_text}</div>
                {['a', 'b', 'c', 'd'].map(opt => (
                  q['option_' + opt] && (
                    <div
                      key={opt}
                      className={`quiz-option ${answers[q.id] === opt ? 'selected' : ''}`}
                      onClick={() => setAnswers(prev => ({ ...prev, [q.id]: opt }))}
                    >
                      <span style={{ marginRight: 8, fontWeight: 600 }}>{opt.toUpperCase()}.</span>
                      {q['option_' + opt]}
                    </div>
                  )
                ))}
              </div>
            ))}
            <button
              className="btn btn-primary"
              style={{ width: '100%', padding: 13, fontSize: 14 }}
              onClick={submitQuiz}
              disabled={Object.keys(answers).length < questions.length || submitting}
            >
              {submitting ? 'Submitting...' : `Submit Quiz (${Object.keys(answers).length}/${questions.length} answered)`}
            </button>
          </>
        )}
      </div>
    );
  }

  return (
    <div>
      {confirm && (
        <div className="modal-overlay" onClick={() => setConfirm(null)}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="confirm-modal">
              <div className="confirm-icon">🗑️</div>
              <div className="confirm-title">Delete Quiz</div>
              <div className="confirm-sub">Delete "{confirm.title}"? This cannot be undone.</div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                <button className="btn btn-secondary" onClick={() => setConfirm(null)}>Cancel</button>
                <button className="btn btn-danger" onClick={() => handleDelete(confirm.id)}>Yes, Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="section-header">
        <div className="section-title">Quizzes</div>
        {isAdmin && <button className="btn btn-primary" onClick={() => { setFormErr(''); setForm({ title: '', videoId: videos[0]?.id || '', passScore: 75, questions: [{ question_text: '', option_a: '', option_b: '', option_c: '', option_d: '', correct_option: 'a' }] }); setModal(true); }}>+ Create Quiz</button>}
      </div>

      {isAdmin ? (
        // Admin table view
        <div className="card">
          {rows.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--muted)', fontSize: 13 }}>No quizzes yet.</div>
          ) : (
            <table>
              <thead><tr><th>Quiz</th><th>Linked Video</th><th>Questions</th><th>Pass Score</th><th>Avg Score</th><th>Attempts</th><th style={{ textAlign: 'right' }}>Actions</th></tr></thead>
              <tbody>
                {rows.map(q => (
                  <tr key={q.id}>
                    <td style={{ fontWeight: 500 }}>{q.title}</td>
                    <td style={{ fontSize: 12, color: 'var(--muted)' }}>{q.video_title || '-'}</td>
                    <td><span className="badge badge-blue">{q.question_count || 0} Qs</span></td>
                    <td><span className="badge badge-amber">{q.pass_score}%</span></td>
                    <td><span className={`badge ${q.avg_score >= q.pass_score ? 'badge-green' : q.avg_score > 0 ? 'badge-red' : 'badge-gray'}`}>{q.avg_score > 0 ? q.avg_score + '%' : 'N/A'}</span></td>
                    <td style={{ color: 'var(--muted)', fontSize: 12 }}>{q.total_attempts || 0}</td>
                    <td style={{ textAlign: 'right' }}><button className="btn-icon" onClick={() => setConfirm({ id: q.id, title: q.title })}>🗑️</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : (
        // Employee card view — reflects attempt history
        <div>
          {rows.length === 0 && <div className="card" style={{ textAlign: 'center', padding: 32, color: 'var(--muted)' }}>No quizzes yet.</div>}
          {rows.map(q => {
            const att = myAttempts[q.id];
            const passed = att && att.passed;
            const failed = att && !att.passed;
            let badgeClass = 'badge-teal';
            let badgeText = 'Available';
            if (passed)  { badgeClass = 'badge-green'; badgeText = `Passed · ${att.score}%`; }
            if (failed)  { badgeClass = 'badge-red';   badgeText = `Failed · ${att.score}%`; }

            return (
              <div key={q.id} className="card" style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 700 }}>{q.title}</div>
                    <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>
                      {q.video_title || '-'} · {q.question_count || 0} questions · Pass: {q.pass_score}%
                      {att && ` · ${att.attempts} attempt${att.attempts > 1 ? 's' : ''}`}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span className={`badge ${badgeClass}`}>{badgeText}</span>
                    <button
                      className={passed ? 'btn btn-secondary btn-sm' : 'btn btn-primary btn-sm'}
                      onClick={() => startQuiz(q)}
                    >
                      {passed ? 'Retake' : failed ? 'Retry Quiz →' : 'Start Quiz →'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {modal && (
        <div className="modal-overlay" onClick={() => setModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><span className="modal-title">Create Quiz</span><button className="close-btn" onClick={() => setModal(false)}>×</button></div>
            {formErr && <div className="alert">{formErr}</div>}
            <div className="form-group"><label className="form-label">Quiz Title *</label><input className="form-input" placeholder="e.g. Safety Knowledge Check" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} /></div>
            <div className="grid-2">
              <div className="form-group"><label className="form-label">Link to Video *</label>
                <select className="form-select" value={form.videoId} onChange={e => setForm(f => ({ ...f, videoId: e.target.value }))}>
                  <option value="">— Select a video —</option>
                  {videos.map(v => <option key={v.id} value={v.id}>{v.title}</option>)}
                </select>
              </div>
              <div className="form-group"><label className="form-label">Pass Score (%)</label><input className="form-input" type="number" min="1" max="100" value={form.passScore} onChange={e => setForm(f => ({ ...f, passScore: e.target.value }))} /></div>
            </div>
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16, marginBottom: 8 }}>
              <div style={{ fontWeight: 600, marginBottom: 12, fontSize: 13 }}>Questions</div>
              {form.questions.map((q, qi) => (
                <div key={qi} style={{ background: 'var(--bg)', borderRadius: 8, padding: 12, marginBottom: 10, position: 'relative' }}>
                  {form.questions.length > 1 && <button onClick={() => removeQuestion(qi)} style={{ position: 'absolute', top: 8, right: 8, background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: 'var(--muted)' }}>✕</button>}
                  <div className="form-group" style={{ marginBottom: 8 }}>
                    <label className="form-label" style={{ fontSize: 12 }}>Q{qi + 1}</label>
                    <input className="form-input" placeholder="Question text..." value={q.question_text} onChange={e => { const qs = [...form.questions]; qs[qi] = { ...qs[qi], question_text: e.target.value }; setForm(f => ({ ...f, questions: qs })); }} />
                  </div>
                  {['a', 'b', 'c', 'd'].map(opt => (
                    <div key={opt} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <input type="radio" checked={q.correct_option === opt} onChange={() => { const qs = [...form.questions]; qs[qi] = { ...qs[qi], correct_option: opt }; setForm(f => ({ ...f, questions: qs })); }} title="Mark as correct" />
                      <input className="form-input" style={{ flex: 1, fontSize: 12, padding: '6px 10px' }} placeholder={`Option ${opt.toUpperCase()}`} value={q['option_' + opt]} onChange={e => { const qs = [...form.questions]; qs[qi] = { ...qs[qi], ['option_' + opt]: e.target.value }; setForm(f => ({ ...f, questions: qs })); }} />
                    </div>
                  ))}
                  <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>Select radio = correct answer</div>
                </div>
              ))}
              <button className="btn btn-secondary btn-sm" onClick={addQuestion}>+ Add Question</button>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
              <button className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleCreate}>Create Quiz</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
